import { store } from "../redux/store";
import { Tool } from "../types/types";
import {
  addDoc,
  collection,
  db,
  deleteDoc,
  doc,
  getCollectionCount as getCollectionCountF,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "../firebase";
import { FirebaseFirestoreTypes } from "@react-native-firebase/firestore";

export type Where = {
  field: string;
  operator: FirebaseFirestoreTypes.WhereFilterOp;
  value: any;
  from?: string;
}[];

export interface QueryOptions {
  where?: Where;
  orderBy?: { field: string; direction?: "asc" | "desc" }[];
  limit?: number;
  onlyCompany?: boolean;
}

export const getCollectionCount = async (
  collectionName: string,
  options?: QueryOptions,
): Promise<number> => {
  try {
    // Get companyId from global state if needed
    const storeState = store.getState().auth.user;
    const companyId = storeState.company?.id;

    // Build raw constraint data (not SDK-specific query constraints)
    const constraintData: { field: string; operator: any; value: any }[] = [];
    constraintData.push({ field: "deleted", operator: "==", value: false });

    if (companyId && options?.onlyCompany !== false) {
      constraintData.push({
        field: "companyId",
        operator: "==",
        value: companyId,
      });
    }

    // Apply additional where conditions if provided
    if (options?.where) {
      options.where.forEach(({ field, operator, value }) => {
        constraintData.push({ field, operator, value });
      });
    }

    // Note: Count queries don't support orderBy or limit
    // Pass raw constraint data to platform-specific count function
    // Each platform will build the query using its own SDK
    return await getCollectionCountF(collectionName, constraintData);
  } catch (error) {
    console.error("Failed to get collection count:", collectionName, error);
    return 0;
  }
};

export const getCompanyDocuments = async (
  collectionName: string,
  options?: QueryOptions,
): Promise<any> => {
  try {
    // Get companyId from global state
    const storeState = store.getState().auth.user;
    const companyId = storeState.company?.id;
    const companyType = storeState.company?.type;

    // Create the collection reference
    const collectionRef = collection(db, collectionName);

    // Build the query
    let queryConstraints = [];
    queryConstraints.push(where("deleted", "==", false));

    if (companyId) {
      queryConstraints.push(where("companyId", "==", companyId));
    }

    // Apply additional where conditions if provided
    if (options?.where) {
      options.where.forEach(({ field, operator, value }) => {
        queryConstraints.push(where(field, operator, value));
      });
    }

    // Apply sorting if provided
    if (options?.orderBy) {
      options.orderBy.forEach(({ field, direction }) => {
        queryConstraints.push(orderBy(field, direction || "asc"));
      });
    }

    // Apply limit if provided
    if (options?.limit) {
      queryConstraints.push(limit(options.limit));
    }

    // Log the query details
    console.log("getCompanyDocuments query details:", {
      collectionName,
      companyId,
      companyType,
      queryConstraintsCount: queryConstraints.length,
      options,
    });

    // Execute the query
    const q = query(collectionRef, ...queryConstraints);
    const snapshot = await getDocs(q);

    // Return the mapped documents
    return snapshot.docs.map((doc: any) => ({ ...doc.data(), id: doc.id }));
  } catch (error) {
    console.error(
      "Failed to retrieve data from collection:",
      collectionName,
      error,
    );
    return [];
  }
};

export const getDocuments = async (
  collectionName: string,
  options?: QueryOptions,
): Promise<any> => {
  try {
    const collectionRef = collection(db, collectionName);

    // Build the query
    let queryConstraints = [];
    queryConstraints.push(where("deleted", "==", false));

    // Apply additional where conditions if provided
    if (options?.where) {
      options.where.forEach(({ field, operator, value }) => {
        queryConstraints.push(where(field, operator, value));
      });
    }

    // Apply sorting if provided
    if (options?.orderBy) {
      options.orderBy.forEach(({ field, direction }) => {
        queryConstraints.push(orderBy(field, direction || "asc"));
      });
    }

    // Apply limit if provided
    if (options?.limit) {
      queryConstraints.push(limit(options.limit));
    }

    // Execute the query
    const q = query(collectionRef, ...queryConstraints);
    const snapshot = await getDocs(q);

    // Return the mapped documents
    return snapshot.docs.map(
      (doc: { data: () => Tool; id: any }) =>
        ({ ...doc.data(), id: doc.id }) as Tool,
    );
  } catch (error) {
    console.error(
      "Failed to retrieve data from collection:",
      collectionName,
      error,
    );
    return [];
  }
};

export const listenCompanyDocuments = (
  collectionName: string,
  onUpdate: (data: any[]) => void,
  options?: QueryOptions,
) => {
  try {
    // Get companyId from global state
    const storeState = store.getState().auth.user;
    const companyId = storeState.company?.id;
    const companyType = storeState.company?.type;

    // Create the collection reference
    const collectionRef = collection(db, collectionName);

    // Build the query
    let queryConstraints = [];
    queryConstraints.push(where("deleted", "==", false));

    if (companyId) {
      queryConstraints.push(where("companyId", "==", companyId));
    }

    // Apply additional where conditions if provided
    if (options?.where) {
      options.where.forEach(({ field, operator, value }) => {
        queryConstraints.push(where(field, operator, value));
      });
    }

    // Apply sorting if provided
    if (options?.orderBy) {
      options.orderBy.forEach(({ field, direction }) => {
        queryConstraints.push(orderBy(field, direction || "asc"));
      });
    }

    // Apply limit if provided
    if (options?.limit) {
      queryConstraints.push(limit(options.limit));
    }

    // Create the query
    const q = query(collectionRef, ...queryConstraints);

    // Return the unsubscribe function
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc: any) => ({
        ...doc.data(),
        id: doc.id,
      }));
      onUpdate(data);
    });
  } catch (error) {
    console.error(
      "Failed to set up listener for collection:",
      collectionName,
      error,
    );
    return () => {}; // Return empty function if there's an error
  }
};

export const listenDocuments = (
  collectionName: string,
  onUpdate: (data: any[]) => void,
  options?: QueryOptions,
) => {
  try {
    const collectionRef = collection(db, collectionName);

    // Build the query
    let queryConstraints = [];
    queryConstraints.push(where("deleted", "==", false));

    // Add company filter if onlyCompany is true
    if (options?.onlyCompany) {
      const storeState = store.getState().auth.user;
      const companyId = storeState.company?.id;

      if (companyId) {
        queryConstraints.push(where("companyId", "==", companyId));
      }
    }

    // Apply additional where conditions if provided
    if (options?.where) {
      options.where.forEach(({ field, operator, value }) => {
        queryConstraints.push(where(field, operator, value));
      });
    }

    // Apply sorting if provided
    if (options?.orderBy) {
      options.orderBy.forEach(({ field, direction }) => {
        queryConstraints.push(orderBy(field, direction || "asc"));
      });
    }

    // Apply limit if provided
    if (options?.limit) {
      queryConstraints.push(limit(options.limit));
    }

    // Create the query
    const q = query(collectionRef, ...queryConstraints);

    // Set up the real-time listener
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      onUpdate(data);
    });

    // Return the unsubscribe function
    return unsubscribe;
  } catch (error) {
    console.error(
      "Failed to set up listener for collection:",
      collectionName,
      error,
    );
    return () => {}; // Return empty function if there's an error
  }
};

const addUniqueIdNumber = async (
  collectionName: string,
  data: Record<string, any>,
) => {
  const countField = collectionName;
  const counterRef = doc(db, "data", "info");

  data["numberId"] = await runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);

    if (counterDoc.exists()) {
      const counterData = counterDoc.data()!;
      const currentNumber = counterData[countField] || 0;
      counterData[countField] = currentNumber + 1;
      transaction.update(counterRef, counterData);
      return currentNumber + 1;
    }
    return 1;
  });
};

const addDefaultFields = (
  data: Record<string, any>,
  includeCompany = false,
) => {
  const storeState = store.getState().auth.user;
  const userId = storeState.uid;
  const companyId = storeState.company?.id;

  // Add deleted: false if not present
  if (data.deleted === undefined) {
    data.deleted = false;
  }

  // Add addedBy if not present
  if (userId && !data.addedBy) {
    data.addedBy = userId;
  }

  // Add companyId if requested and not present
  if (includeCompany && companyId && !data.companyId) {
    data.companyId = companyId;
  }
};

export const addDocumentWithoutCompany = async (
  collectionParams: { uniqIdNumber?: boolean; name: string; fields?: any },
  data: Record<string, any>,
) => {
  console.log("collection params", collectionParams);

  addDefaultFields(data, false);

  if (collectionParams.uniqIdNumber) {
    try {
      await addUniqueIdNumber(collectionParams.name, data);
      console.log("data", data);
      const docRef = collection(db, collectionParams.name);
      return addDoc(docRef, data);
    } catch (error) {
      console.error("Failed to add document to tickets:", error);
      throw error;
    }
  }

  const collectionRef = collection(db, collectionParams.name);
  return addDoc(collectionRef, data);
};

export const addDocument = async (
  collectionParams: { uniqIdNumber?: boolean; name: string; fields?: any },
  data: Record<string, any>,
) => {
  console.log("collection params", collectionParams);

  addDefaultFields(data, true);

  if (collectionParams.uniqIdNumber) {
    try {
      await addUniqueIdNumber(collectionParams.name, data);
      console.log("data", data);
      const docRef = collection(db, collectionParams.name);
      return addDoc(docRef, data);
    } catch (error) {
      console.error("Failed to add document to tickets:", error);
      throw error;
    }
  }

  const collectionRef = collection(db, collectionParams.name);
  return addDoc(collectionRef, data);
};

export const update = async (
  collectionName: string,
  id: string,
  data: Record<string, any>,
): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, id);
    const userId = store.getState().auth.user?.uid;

    const newData = {
      ...data,
      updatedAt: data.updatedAt ?? serverTimestamp(),
      updatedBy: userId,
    };
    await updateDoc(docRef, newData);
  } catch (error) {
    console.error(`Failed to update document in ${collectionName}:`, error);
    throw error;
  }
};

export const getDocument = async (collectionName: string, id: string) => {
  try {
    const docRef = doc(db, collectionName, id);
    const snapshot = await getDoc(docRef);
    if (snapshot.exists) {
      return { ...snapshot.data(), id: snapshot.id };
    } else {
      console.error(`No document found in ${collectionName} with ID: ${id}`);
      return null;
    }
  } catch (error) {
    console.error(`Failed to retrieve document from ${collectionName}:`, error);
    throw error;
  }
};

export const setDocument = async (
  collectionName: string,
  id: string,
  data: Record<string, any>,
): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, id);
    await setDoc(docRef, data);
  } catch (error) {
    console.error(
      `Failed to set document in ${collectionName} with ID ${id}:`,
      error,
    );
    throw error;
  }
};

export const deleteDocument = async (
  collection: any,
  id: string,
): Promise<void> => {
  try {
    const docRef = doc(db, collection.name, id);
    await deleteDoc(docRef);
    console.log(`Document with ID ${id} deleted from ${collection.name}`);
  } catch (error) {
    console.error(
      `Failed to delete document from ${collection.name} with ID ${id}:`,
      error,
    );
    throw error;
  }
};

export const listenToDocuments = (
  collectionName: string,
  onUpdate: (data: any[]) => void,
  options?: QueryOptions,
) => {
  try {
    const collectionRef = collection(db, collectionName);
    let queryConstraints = [];
    queryConstraints.push(where("deleted", "==", false));

    // Add company filter if onlyCompany is true
    if (options?.onlyCompany) {
      const user = store.getState().auth.user;
      const companyId = user.company?.id;

      if (companyId) {
        queryConstraints.push(where("companyId", "==", companyId));
      }
    }

    // Apply additional where conditions if provided
    if (options?.where) {
      options.where.forEach(({ field, operator, value }) => {
        queryConstraints.push(where(field, operator, value));
      });
    }

    // Apply sorting if provided
    if (options?.orderBy) {
      options.orderBy.forEach(({ field, direction }) => {
        queryConstraints.push(orderBy(field, direction || "asc"));
      });
    }

    // Apply limit if provided
    if (options?.limit) {
      queryConstraints.push(limit(options.limit));
    }

    // Create the query
    const q = query(collectionRef, ...queryConstraints);

    // Set up the real-time listener
    // Return the unsubscribe function
    return onSnapshot(q, (snapshot) => {
      console.log("board ===> snapshot.size", snapshot.size);
      const data = snapshot.docs
        .map((doc: any) => ({ ...doc.data(), id: doc.id }) as any)
        .sort((a, b) => b.createdAt - a.createdAt);
      onUpdate(data);
    });
  } catch (error) {
    console.error(
      "Failed to set up listener for collection:",
      collectionName,
      error,
    );
    return () => {}; // Return empty function if there's an error
  }
};
